import React, { useState, useEffect, useRef } from 'react';
import { X, Search, Play, Pause, Check } from 'lucide-react';
import { useEscapeKey } from '../../hooks/useEscapeKey';

interface Voice {
  voice_id: string;
  voice_name: string;
  accent: string;
  gender: string;
  age: string;
  avatar_url: string;
  preview_audio_url: string;
  recommended?: boolean;
}

interface VoiceSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectVoice: (voiceId: string) => void;
  currentVoiceId?: string;
}

const VoiceSelectionModal: React.FC<VoiceSelectionModalProps> = ({
  isOpen,
  onClose,
  onSelectVoice,
  currentVoiceId
}) => {
  const [voices, setVoices] = useState<Voice[]>([]);
  const [filteredVoices, setFilteredVoices] = useState<Voice[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGender, setSelectedGender] = useState<string>('all');
  const [selectedAccent, setSelectedAccent] = useState<string>('all');
  const [selectedAge, setSelectedAge] = useState<string>('all');
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEscapeKey(onClose, isOpen);

  // Load voices from JSON
  useEffect(() => {
    const loadVoices = async () => {
      try {
        const response = await fetch('/voice-selection.json');
        const data = await response.json();
        
        // Filter only ElevenLabs voices
        const elevenlabsVoices = data.filter((voice: any) => voice.provider === 'elevenlabs');
        setVoices(elevenlabsVoices);
        setFilteredVoices(elevenlabsVoices);
      } catch (error) {
        console.error('Failed to load voices:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      loadVoices();
    }
  }, [isOpen]);

  // Filter voices based on search and filters
  useEffect(() => {
    let filtered = [...voices];

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(voice =>
        voice.voice_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        voice.accent.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply gender filter
    if (selectedGender !== 'all') {
      filtered = filtered.filter(voice =>
        voice.gender.toLowerCase() === selectedGender.toLowerCase()
      );
    }

    // Apply accent filter
    if (selectedAccent !== 'all') {
      filtered = filtered.filter(voice =>
        voice.accent.toLowerCase().includes(selectedAccent.toLowerCase())
      );
    }

    // Apply age filter
    if (selectedAge !== 'all') {
      filtered = filtered.filter(voice =>
        voice.age.toLowerCase().includes(selectedAge.toLowerCase())
      );
    }

    // Sort: recommended first, then alphabetically
    filtered.sort((a, b) => {
      if (a.recommended && !b.recommended) return -1;
      if (!a.recommended && b.recommended) return 1;
      return a.voice_name.localeCompare(b.voice_name);
    });

    setFilteredVoices(filtered);
  }, [voices, searchQuery, selectedGender, selectedAccent, selectedAge]);

  // Get unique values for filters
  const genders = Array.from(new Set(voices.map(v => v.gender))).sort();
  const accents = Array.from(new Set(voices.map(v => v.accent))).sort();
  const ages = Array.from(new Set(voices.map(v => v.age))).sort();

  // Play/pause audio preview
  const handlePlayPause = (voice: Voice) => {
    if (playingVoiceId === voice.voice_id) {
      // Pause current audio
      audioRef.current?.pause();
      setPlayingVoiceId(null);
    } else {
      // Stop current audio if playing
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }

      // Play new audio
      const audio = new Audio(voice.preview_audio_url);
      audio.onended = () => setPlayingVoiceId(null);
      audio.play();
      audioRef.current = audio;
      setPlayingVoiceId(voice.voice_id);
    }
  };

  // Clean up audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Select Voice</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Filters */}
        <div className="p-6 border-b border-gray-200 space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search voices..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Filter dropdowns */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Gender Filter */}
            <select
              value={selectedGender}
              onChange={(e) => setSelectedGender(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Genders</option>
              {genders.map(gender => (
                <option key={gender} value={gender}>{gender}</option>
              ))}
            </select>

            {/* Accent Filter */}
            <select
              value={selectedAccent}
              onChange={(e) => setSelectedAccent(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Accents</option>
              {accents.map(accent => (
                <option key={accent} value={accent}>{accent}</option>
              ))}
            </select>

            {/* Age Filter */}
            <select
              value={selectedAge}
              onChange={(e) => setSelectedAge(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Ages</option>
              {ages.map(age => (
                <option key={age} value={age}>{age}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Voice List */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredVoices.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No voices found matching your criteria
            </div>
          ) : (
            <div className="space-y-3">
              {filteredVoices.map((voice) => (
                <div
                  key={voice.voice_id}
                  className={`flex items-center gap-4 p-4 rounded-lg border transition-all ${
                    currentVoiceId === voice.voice_id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {/* Play Button */}
                  <button
                    onClick={() => handlePlayPause(voice)}
                    className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-400 hover:bg-gray-500 text-white flex items-center justify-center transition-colors"
                  >
                    {playingVoiceId === voice.voice_id ? (
                      <Pause className="w-4 h-4" />
                    ) : (
                      <Play className="w-4 h-4 ml-0.5" />
                    )}
                  </button>

                  {/* Avatar */}
                  <img
                    src={voice.avatar_url}
                    alt={voice.voice_name}
                    className="w-12 h-12 rounded-full object-cover"
                    onError={(e) => {
                      // Fallback to a default avatar if image fails to load
                      (e.target as HTMLImageElement).src = 'https://via.placeholder.com/48';
                    }}
                  />

                  {/* Voice Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900">
                      {voice.voice_name}
                    </h3>
                    <div className="flex items-center gap-2 mt-1 text-sm text-gray-600">
                      <span>{voice.accent}</span>
                      <span>•</span>
                      <span>{voice.age}</span>
                      <span>•</span>
                      <span className="capitalize">{voice.gender}</span>
                    </div>
                  </div>

                  {/* Voice ID (for reference) */}
                  <div className="hidden lg:block text-sm text-gray-500 font-mono">
                    {voice.voice_id}
                  </div>

                  {/* Select Button */}
                  <button
                    onClick={() => onSelectVoice(voice.voice_id)}
                    disabled={currentVoiceId === voice.voice_id}
                    className={`flex-shrink-0 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      currentVoiceId === voice.voice_id
                        ? 'bg-green-100 text-green-700 cursor-default'
                        : 'bg-gray-500 text-white hover:bg-gray-600'
                    }`}
                  >
                    {currentVoiceId === voice.voice_id ? (
                      <span className="flex items-center gap-2">
                        <Check className="w-4 h-4" />
                        Current
                      </span>
                    ) : (
                      'Use Voice'
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Showing {filteredVoices.length} of {voices.length} voices
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoiceSelectionModal;

